import React, { useState } from 'react';
import {
  TextField,
  Switch,
  IconButton,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from '@material-ui/core';
import { Add, ExpandMore, Delete } from '@material-ui/icons';

const JsonUIModeEditor = ({
  data,
  onChange,
  depth = 0,
  parentWhite = true
}) => {
  const [addingPath, setAddingPath] = useState(null);
  const [addingValue, setAddingValue] = useState('');
  const [addingTop, setAddingTop] = useState(false);

  const handleChange = (path, value) => {
    const updated = { ...data };
    let obj = updated;
    for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
    obj[path[path.length - 1]] = value;
    onChange(updated);
  };

  const handleDelete = path => {
    const updated = { ...data };
    let obj = updated;
    for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
    const lastKey = path[path.length - 1];
    if (Array.isArray(obj)) obj.splice(lastKey, 1);
    else delete obj[lastKey];
    onChange(updated);
  };

  const getBgColor = isWhite => (isWhite ? '#ffffff' : '#f0f0f0');

  const getDataType = value => {
    if (Array.isArray(value)) return 'array';
    if (value === null) return 'null';
    return typeof value;
  };

  const renderField = (key, value, path, parentWhite) => {
    const dataType = getDataType(value);
    const bgColor = getBgColor(parentWhite);
    const childWhite = !parentWhite; // children are opposite of parent

    if (
      dataType === 'boolean' ||
      dataType === 'number' ||
      dataType === 'string' ||
      dataType === 'null'
    ) {
      return (
        <Box
          key={key}
          mb={1}
          display="flex"
          alignItems="center"
          bgcolor={bgColor}
          p={1}
          borderRadius={4}
        >
          {dataType === 'boolean' ? (
            <>
              <label>{key} (Boolean)</label>
              <Switch
                checked={value}
                onChange={e => handleChange(path, e.target.checked)}
              />
            </>
          ) : (
            <TextField
              label={`${key} (${dataType})`}
              value={value ?? ''}
              type={dataType === 'number' ? 'number' : 'text'}
              fullWidth
              onChange={e => {
                let newVal = e.target.value;
                if (
                  dataType === 'number' &&
                  !isNaN(Number(newVal)) &&
                  newVal !== ''
                )
                  newVal = Number(newVal);
                handleChange(path, newVal);
              }}
            />
          )}
          <IconButton
            size="small"
            onClick={() => handleDelete(path)}
            style={{ marginLeft: 'auto' }}
          >
            <Delete />
          </IconButton>
        </Box>
      );
    }

    if (dataType === 'array' || dataType === 'object') {
      const isArray = dataType === 'array';
      const childrenKeys = isArray
        ? value.map((_, idx) => idx.toString())
        : Object.keys(value);

      const children = childrenKeys.map((k, idx) => {
        const childPath = [...path, isArray ? Number(k) : k];
        return renderField(
          k,
          isArray ? value[idx] : value[k],
          childPath,
          childWhite
        );
      });

      return (
        <Accordion
          key={key}
          defaultExpanded
          style={{ backgroundColor: bgColor }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>{`${key} (${
              isArray ? 'Array' : 'Object'
            })`}</Typography>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                setAddingPath(path);
              }}
              style={{ marginLeft: 'auto' }}
            >
              <Add />
            </IconButton>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                handleDelete(path);
              }}
            >
              <Delete />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails style={{ display: 'block', padding: 8 }}>
            {children}
            {addingPath?.join() === path.join() && (
              <TextField
                autoFocus
                value={addingValue}
                fullWidth
                placeholder={
                  isArray
                    ? 'Enter value (or JSON)'
                    : 'Enter key:value (JSON ok)'
                }
                onChange={e => setAddingValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    if (isArray) {
                      let newVal = addingValue.trim();
                      if (!newVal) return;
                      try {
                        newVal = JSON.parse(newVal);
                      } catch {}
                      handleChange(path, [...value, newVal]);
                    } else {
                      try {
                        const parsed = JSON.parse(`{${addingValue}}`);
                        handleChange(path, { ...value, ...parsed });
                      } catch {}
                    }
                    setAddingPath(null);
                    setAddingValue('');
                  }
                }}
                style={{ marginTop: 4 }}
              />
            )}
          </AccordionDetails>
        </Accordion>
      );
    }

    return null;
  };

  return (
    <div
      style={{
        maxHeight: '80vh',
        overflowY: 'auto',
        padding: 8,
        borderRadius: 4,
        backgroundColor: '#fff'
      }}
    >
      {!addingTop && (
        <Box mb={1}>
          <IconButton size="small" onClick={() => setAddingTop(true)}>
            <Add />
          </IconButton>
          <Typography component="span">Add top-level key</Typography>
        </Box>
      )}
      {addingTop && (
        <TextField
          autoFocus
          value={addingValue}
          fullWidth
          placeholder="Enter key:value (JSON ok)"
          onChange={e => setAddingValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              let val = addingValue.trim();
              if (!val) return;
              try {
                val = JSON.parse(`{${val}}`);
              } catch {}
              onChange({ ...data, ...val });
              setAddingValue('');
              setAddingTop(false);
            }
          }}
          style={{ marginBottom: 8 }}
        />
      )}

      {Object.keys(data).map(k => renderField(k, data[k], [k], true))}
    </div>
  );
};

export default JsonUIModeEditor;
